import { api } from './api';
import { AdminUser, CreateAdminUserDto, AdminUserResponse } from '../types/admin';

/**
 * Get list of all admin users (requires ADMIN or SUPER_ADMIN role)
 */
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const response = await api.get('/auth/admin/users');
  const body = response.data;

  // Direct array response
  if (Array.isArray(body)) {
    return body;
  }

  if (body && typeof body === 'object') {
    // { data: [...] } or { success, data: [...] }
    if (Array.isArray(body.data)) {
      return body.data;
    }
    // { data: { items: [...] } } or { data: { users: [...] } }
    if (body.data && typeof body.data === 'object') {
      if (Array.isArray(body.data.items)) return body.data.items;
      if (Array.isArray(body.data.users)) return body.data.users;
    }
    // { items: [...] } or { users: [...] } or { admins: [...] }
    if (Array.isArray(body.items)) return body.items;
    if (Array.isArray(body.users)) return body.users;
    if (Array.isArray(body.admins)) return body.admins;
  }

  // Fallback to empty array — log full shape for debugging
  console.warn(
    'Unexpected response format from /auth/admin/users:',
    JSON.stringify(body, null, 2)?.slice(0, 2000),
    'keys:', body ? Object.keys(body) : 'null'
  );
  return [];
};

/**
 * Create a new admin user (requires ADMIN or SUPER_ADMIN role)
 */
// Helper to unwrap { success, data } API response envelope
function unwrap<T>(body: any): T {
  if (body && typeof body === 'object' && 'data' in body && !Array.isArray(body)) {
    return body.data as T;
  }
  return body as T;
}

export const createAdminUser = async (data: CreateAdminUserDto): Promise<AdminUserResponse> => {
  const response = await api.post('/auth/admin/users', data);
  return unwrap<AdminUserResponse>(response.data);
};

/**
 * Get current admin user info
 */
export const getCurrentAdminUser = async (): Promise<AdminUser> => {
  const response = await api.get('/auth/admin/me');
  return unwrap<AdminUser>(response.data);
};

/**
 * Deactivate an admin user (requires ADMIN or SUPER_ADMIN role)
 */
export const deactivateAdminUser = async (userId: number): Promise<{ message: string }> => {
  const response = await api.delete(`/auth/admin/users/${userId}`);
  return unwrap<{ message: string }>(response.data);
};

/**
 * Reactivate an admin user (requires SUPER_ADMIN role)
 */
export const reactivateAdminUser = async (userId: number): Promise<AdminUser> => {
  const response = await api.patch(`/auth/admin/users/${userId}/reactivate`);
  return unwrap<AdminUser>(response.data);
};

/**
 * Unlock an admin user account (requires SUPER_ADMIN role)
 */
export const unlockAdminUser = async (userId: number): Promise<AdminUser> => {
  const response = await api.patch(`/auth/admin/users/${userId}/unlock`);
  return unwrap<AdminUser>(response.data);
};
